import type { ApplicationFilters, ApplicationTab } from './types';

import { filterSchema } from './filter-schema';

export const TAB_PARAM = 't';

export const SEARCH_PARAM = 'q';

export const PAGE_PARAM = 'page';

export const FILTER_PARAMS: (keyof ApplicationFilters)[] = [
  'setupStatus',
  'tasks',
  'productIncluded',
  'assuranceReached',
];

const FILTER_VALUE_SEPARATOR = ',';

const DEFAULT_TAB: ApplicationTab = 'all';

const DEFAULT_PAGE = 1;

const APPLICATION_TABS: ApplicationTab[] = [
  'all',
  'unread_tasks',
  'your_applications',
  'vendor_applications',
  'unclaimed',
];

const isApplicationTab = (value: string | null): value is ApplicationTab =>
  APPLICATION_TABS.includes(value as ApplicationTab);

function parsePage(raw: string | null): number {
  const page = Number(raw);

  return Number.isInteger(page) && page > 0 ? page : DEFAULT_PAGE;
}

type ApplicationsSearchParams = {
  tab: ApplicationTab;
  search: string;
  page: number;
  filters: ApplicationFilters;
};

type ApplicationsSearchParamsPatch = {
  tab?: ApplicationTab;
  search?: string;
  page?: number;
  filters?: Partial<Record<keyof ApplicationFilters, string[] | undefined>>;
};

function parseFilterValues(key: keyof ApplicationFilters, raw: string | null): string[] | undefined {
  const itemSchema = filterSchema.shape[key].unwrap().element;
  const values = raw?.split(FILTER_VALUE_SEPARATOR).filter((value) => itemSchema.safeParse(value).success);

  return values?.length ? values : undefined;
}

function parseFilters(params: URLSearchParams): ApplicationFilters {
  return Object.fromEntries(
    FILTER_PARAMS.map((key) => [key, parseFilterValues(key, params.get(key))]),
  ) as ApplicationFilters;
}

export function parseApplicationsSearchParams(params: URLSearchParams): ApplicationsSearchParams {
  const tabParam = params.get(TAB_PARAM);

  return {
    tab: isApplicationTab(tabParam) ? tabParam : DEFAULT_TAB,
    search: params.get(SEARCH_PARAM) ?? '',
    page: parsePage(params.get(PAGE_PARAM)),
    filters: parseFilters(params),
  };
}

export function updateApplicationsSearchParams(
  prev: URLSearchParams,
  patch: ApplicationsSearchParamsPatch,
): URLSearchParams {
  const next = new URLSearchParams(prev);

  // A new tab, search or filter changes the result set, so the page starts over.
  if (patch.tab !== undefined || patch.search !== undefined || patch.filters !== undefined) next.delete(PAGE_PARAM);

  if (patch.page === DEFAULT_PAGE) next.delete(PAGE_PARAM);
  else if (patch.page !== undefined) next.set(PAGE_PARAM, String(patch.page));

  if (patch.tab === DEFAULT_TAB) next.delete(TAB_PARAM);
  else if (patch.tab !== undefined) next.set(TAB_PARAM, patch.tab);

  if (patch.search === '') next.delete(SEARCH_PARAM);
  else if (patch.search !== undefined) next.set(SEARCH_PARAM, patch.search);

  Object.entries(patch.filters ?? {}).forEach(([key, values]) => {
    if (values?.length) next.set(key, values.join(FILTER_VALUE_SEPARATOR));
    else next.delete(key);
  });

  return next;
}
